import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BUCKET_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#f97316', '#ef4444', '#7c3aed'];

function buildHistogram(timings) {
  const defs = [
    { label: '0–7j',   min: 0,   max: 7   },
    { label: '7–14j',  min: 7,   max: 14  },
    { label: '14–30j', min: 14,  max: 30  },
    { label: '30–60j', min: 30,  max: 60  },
    { label: '60–90j', min: 60,  max: 90  },
    { label: '90j+',   min: 90,  max: Infinity },
  ];
  return defs.map((d, i) => ({
    label: d.label,
    count: timings.filter(v => v >= d.min && v < d.max).length,
    color: BUCKET_COLORS[i],
  }));
}

function computeMedian(arr) {
  if (!arr.length) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

function lastWeekDate() {
  const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  return d.toISOString().split('T')[0];
}

export default function KpiDevToDonePanel({ title, subtitle, fetchFn }) {
  const today = new Date().toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(lastWeekDate());
  const [endDate, setEndDate] = useState(today);
  const [sampleSize, setSampleSize] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleCalculate() {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn(startDate, endDate, sampleSize);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { handleCalculate(); }, []);

  const timings = data?.timings ?? [];
  const histData = timings.length ? buildHistogram(timings) : null;
  const med = computeMedian(timings);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Date début
          </label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Date fin
          </label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Échantillon <span className="font-normal normal-case text-gray-400">(max tickets)</span>
          </label>
          <input
            type="number"
            value={sampleSize}
            min="10"
            max="200"
            step="10"
            onChange={e => setSampleSize(Math.max(10, Math.min(200, Number(e.target.value))))}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={handleCalculate}
          disabled={loading}
          className="px-5 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
        >
          {loading ? 'Calcul…' : 'Calculer'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          Erreur : {error}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center h-36 text-gray-400 gap-3">
          <svg className="animate-spin h-7 w-7 text-blue-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">
            Analyse en cours — jusqu'à {sampleSize} tickets (chaque ticket nécessite un appel API)…
          </span>
        </div>
      )}

      {data && !loading && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border-2 border-blue-200">
              <div className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">
                Temps moyen
              </div>
              <div className="text-5xl font-bold text-blue-700 leading-none">{data.averageTime}</div>
              <div className="text-sm text-blue-500 mt-2">jours</div>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-5 rounded-xl border-2 border-indigo-200">
              <div className="text-xs font-semibold text-indigo-500 uppercase tracking-wide mb-1">
                Médiane
              </div>
              <div className="text-5xl font-bold text-indigo-700 leading-none">{med ?? '–'}</div>
              <div className="text-sm text-indigo-500 mt-2">jours</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border-2 border-green-200">
              <div className="text-xs font-semibold text-green-500 uppercase tracking-wide mb-1">
                Tickets analysés
              </div>
              <div className="text-5xl font-bold text-green-700 leading-none">{timings.length}</div>
              <div className="text-xs text-green-400 mt-2">sur {sampleSize} max</div>
            </div>
          </div>

          {/* Histogram */}
          {histData && histData.some(b => b.count > 0) ? (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Distribution des durées
              </h3>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={histData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                  <Tooltip
                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                    formatter={v => [v + ' ticket' + (v > 1 ? 's' : ''), 'Nombre']}
                    labelFormatter={l => `Durée : ${l}`}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="count" radius={[5, 5, 0, 0]}>
                    {histData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 mt-3 justify-center">
                {histData.map((b, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: b.color }}
                    />
                    {b.label}
                    <span className="font-semibold text-gray-700">({b.count})</span>
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 text-sm py-6">
              Aucun ticket avec les deux dates renseignées sur cette période.
            </div>
          )}
        </>
      )}

      {!data && !loading && (
        <div className="flex flex-col items-center justify-center h-28 text-gray-400 gap-2">
          <span className="text-3xl">📅</span>
          <span className="text-sm">Définissez la période et cliquez sur "Calculer"</span>
        </div>
      )}
    </div>
  );
}
